import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  open: boolean;
  onClose: () => void;
}

// ─── IV timeline visual ──────────────────────────────────────────────────────

const IvTimeline = () => {
  const bars = [
    { label: '4w before', height: 50, color: '#90caf9', note: '' },
    { label: '3w before', height: 60, color: '#90caf9', note: '' },
    { label: '2w before', height: 80, color: '#42a5f5', note: '' },
    { label: '1w before', height: 130, color: '#1976d2', note: '← sell here' },
    { label: 'Earnings', height: 160, color: '#7b1fa2', note: '📅 event' },
    { label: '1d after', height: 65, color: '#ef5350', note: '← buy back' },
    { label: '1w after', height: 45, color: '#e57373', note: 'IV crushed' },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          justifyContent: 'center',
          height: 200,
          px: 1,
        }}
      >
        {bars.map(({ label, height, color, note }) => (
          <Box key={label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
            {note && (
              <Typography
                variant="caption"
                sx={{
                  color: note.startsWith('←') ? 'success.main' : 'text.secondary',
                  fontWeight: note.startsWith('←') ? 700 : 400,
                  textAlign: 'center',
                  fontSize: '0.6rem',
                  height: 28,
                  display: 'flex',
                  alignItems: 'flex-end',
                  lineHeight: 1.2,
                }}
              >
                {note}
              </Typography>
            )}
            {!note && <Box sx={{ height: 28 }} />}
            <Box
              sx={{
                width: '100%',
                height,
                backgroundColor: color,
                borderRadius: '3px 3px 0 0',
              }}
            />
            <Typography
              variant="caption"
              sx={{ fontSize: '0.6rem', textAlign: 'center', mt: 0.5, lineHeight: 1.2, color: 'text.secondary' }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
        Implied Volatility (IV) over time — earnings creates a predictable spike and collapse
      </Typography>
    </Box>
  );
};

// ─── grade card ──────────────────────────────────────────────────────────────

const GradeCard = ({
  grade,
  color,
  bg,
  label,
  edge,
  action,
}: {
  grade: string;
  color: string;
  bg: string;
  label: string;
  edge: string;
  action: string;
}) => (
  <Box sx={{ flex: 1, minWidth: { xs: 'calc(50% - 8px)', sm: 100 }, textAlign: 'center' }}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: bg,
        border: `2px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 0.5,
      }}
    >
      <Typography fontWeight={700} color={color}>{grade}</Typography>
    </Box>
    <Typography variant="caption" fontWeight={700} display="block">{label}</Typography>
    <Typography variant="caption" color="text.secondary" display="block">{edge}</Typography>
    <Typography variant="caption" sx={{ color, fontWeight: 600 }}>{action}</Typography>
  </Box>
);

// ─── main component ───────────────────────────────────────────────────────────

const EarningsGuide: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
  <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>Earnings Volatility Plays</Typography>
        <Typography variant="body2" color="text.secondary">
          Sell the fear premium before earnings — profit from the IV collapse after
        </Typography>
      </Box>
      <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
    </DialogTitle>

    <DialogContent dividers>

      {/* ── big idea ─────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 3, backgroundColor: 'secondary.main', color: '#fff', borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          The one-line idea
        </Typography>
        <Typography variant="body1">
          Every earnings report makes options artificially expensive. We sell that expensive premium just
          before the event, and buy it back cheaply the next day after "IV crush" hits.
        </Typography>
      </Paper>

      {/* ── iv timeline ───────────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        The IV (Implied Volatility) cycle around earnings
      </Typography>
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <IvTimeline />
      </Paper>

      {/* ── what is iv crush ─────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        What is "IV crush"?
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {[
          {
            icon: '😰',
            title: 'Before earnings',
            body: 'Nobody knows what the company will report. Traders pay up for options to protect against surprise moves. IV spikes.',
          },
          {
            icon: '📢',
            title: 'Earnings released',
            body: 'The uncertainty is gone. Even a big move is now a known move. Options buyers no longer need that extra protection.',
          },
          {
            icon: '💨',
            title: 'IV collapses',
            body: 'Option prices drop sharply the next morning regardless of whether the stock went up or down. This is IV crush.',
          },
        ].map(({ icon, title, body }) => (
          <Paper key={title} variant="outlined" sx={{ p: 2, flex: 1, minWidth: { xs: '100%', sm: 160 } }}>
            <Typography fontSize="1.5rem" sx={{ mb: 0.5 }}>{icon}</Typography>
            <Typography variant="body2" fontWeight={700} gutterBottom>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{body}</Typography>
          </Paper>
        ))}
      </Box>

      {/* ── worked example ────────────────────────────────────────────── */}
      <Divider sx={{ mb: 2.5 }} />
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Worked example — iron condor around earnings
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {[
          {
            step: '1',
            color: '#1976d2',
            action: 'Stock is at $100. Sell $95/$90 put spread + $105/$110 call spread (iron condor)',
            outcome: 'Collect $2.10 net credit. Max loss is $2.90 per side ($5 width − $2.10 credit). Risk is fully defined.',
          },
          {
            step: '2',
            color: '#7b1fa2',
            action: 'Earnings reported — stock jumps to $108',
            outcome: 'Stock moved, but stayed inside the short strikes ($95–$105). Both spreads are still out-of-the-money.',
          },
          {
            step: '3',
            color: '#388e3c',
            action: 'Morning after earnings — buy back the spreads',
            outcome: 'IV crushed. The entire condor now costs only $0.30 to close. Profit = $2.10 − $0.30 = $1.80.',
          },
          {
            step: '✓',
            color: '#f57c00',
            action: 'Result',
            outcome: '$180 profit on $290 max risk in ~1 day. 62% return on risk. IV crush did the work.',
          },
        ].map(({ step, color, action, outcome }) => (
          <Box key={step} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 28, height: 28, borderRadius: '50%', backgroundColor: color,
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, mt: 0.3,
              }}
            >
              {step}
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600}>{action}</Typography>
              <Typography variant="body2" color="text.secondary">{outcome}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── grade system ──────────────────────────────────────────────── */}
      <Divider sx={{ mb: 2.5 }} />
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Understanding grades
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pryvo grades each earnings event based on the <strong>IV Edge Ratio</strong> — how much higher
        current IV is versus the stock's typical IV. The higher the ratio, the more premium the market
        is paying you to take on the risk.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-around', mb: 2, flexWrap: 'wrap' }}>
          <GradeCard
            grade="A+"
            color="#1b5e20"
            bg="rgba(27,94,32,0.1)"
            label="Highest conviction"
            edge="IV edge ≥ 2.0× + credit/risk ≥ 20%"
            action="Full size"
          />
          <GradeCard
            grade="A"
            color="#2e7d32"
            bg="rgba(46,125,50,0.1)"
            label="High edge"
            edge="IV edge ≥ 1.8×"
            action="Full size"
          />
          <GradeCard
            grade="B"
            color="#1565c0"
            bg="rgba(21,101,192,0.1)"
            label="Good edge"
            edge="IV edge 1.4–1.8×, credit ≥15%"
            action="Full size"
          />
          <GradeCard
            grade="S"
            color="#757575"
            bg="rgba(117,117,117,0.1)"
            label="Skip"
            edge="IV edge < 1.4× or credit < 15%"
            action="Pass"
          />
        </Box>

        {/* iv edge bar visualisation */}
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          IV Edge Ratio = Current IV ÷ Historical Average IV
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 90, mt: 1 }}>
          {[
            { label: '2.0× A+', height: 80, color: '#1b5e20' },
            { label: '1.8× A', height: 65, color: '#2e7d32' },
            { label: '1.4× B', height: 45, color: '#1565c0' },
            { label: '1.2× C', height: 35, color: '#e65100' },
            { label: '< 1.2× S', height: 20, color: '#bdbdbd', divider: true },
          ].map(({ label, height, color, divider }) => (
            <Box key={label} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ width: '70%', height, backgroundColor: color, borderRadius: '2px 2px 0 0' }} />
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, mt: 0.5 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Above 1.0× = options are more expensive than usual. Higher = bigger edge.
        </Typography>
      </Paper>

      {/* ── strategy types ────────────────────────────────────────────── */}
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Strategies Pryvo recommends
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {/* Credit spreads — top row */}
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
          Credit spreads
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
          {[
            {
              name: 'Iron Condor',
              when: 'All grades (A+/A/B/C)',
              desc: 'Sell an OTM PUT and OTM CALL, then buy further-out options on each side as insurance. Profit if the stock stays between the inner strikes.',
              chips: ['Credit spread', 'Defined risk', 'Neutral', 'Always first'],
            },
            {
              name: 'Bear Call Spread',
              when: 'Grade A+/A/B + bearish sentiment',
              desc: 'Sell an OTM CALL and buy a higher-strike CALL. Profit if the stock stays flat or falls. Chosen when put/call ratio signals bearish lean.',
              chips: ['Credit spread', 'Defined risk', 'Bearish bias'],
            },
            {
              name: 'Bull Put Spread',
              when: 'Grade A+/A/B + neutral/bullish sentiment',
              desc: 'Sell an OTM PUT and buy a lower-strike PUT. Profit if the stock holds steady or rises. Chosen when put/call ratios are neutral or bullish.',
              chips: ['Credit spread', 'Defined risk', 'Bullish bias'],
            },
          ].map(({ name, when, desc, chips }) => (
            <Box key={name} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>{name}</Typography>
              <Typography variant="caption" color="primary.main" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                When: {when}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{desc}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {chips.map(c => <Chip key={c} label={c} size="small" variant="outlined" />)}
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Calendar spreads — bottom row */}
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
          Calendar spreads
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {[
            {
              name: 'Long Put Calendar',
              when: 'Grade A+/A/B + near call-heavy / far put-heavy',
              desc: 'Sell a near-expiry PUT, buy a far-expiry PUT at max pain strike. Triggered when near-term is call-heavy but the 4-week chain is put-heavy and stock is above max pain — bearish lean.',
              chips: ['Debit spread', 'Bearish lean', 'Far expiry liquidity', 'Half size'],
            },
            {
              name: 'Call Calendar',
              when: 'Grade A+/A/B + near put-heavy / far call-heavy',
              desc: 'Sell a near-expiry CALL, buy a far-expiry CALL at max pain strike. Triggered when near-term is put-heavy but the 4-week chain is call-heavy and stock is below max pain — bullish lean.',
              chips: ['Debit spread', 'Bullish lean', 'Far expiry liquidity', 'Half size'],
            },
          ].map(({ name, when, desc, chips }) => (
            <Box key={name} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={700} gutterBottom>{name}</Typography>
              <Typography variant="caption" color="primary.main" fontWeight={600} display="block" sx={{ mb: 0.75 }}>
                When: {when}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{desc}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {chips.map(c => <Chip key={c} label={c} size="small" variant="outlined" />)}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* ── max pain ──────────────────────────────────────────────────── */}
      <Divider sx={{ mb: 2.5 }} />
      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', gap: 2 }}>
        <Typography fontSize="1.8rem">🎯</Typography>
        <Box>
          <Typography variant="body2" fontWeight={700} gutterBottom>Max Pain — picking your strikes</Typography>
          <Typography variant="body2" color="text.secondary">
            Max Pain is the price where the most options expire worthless — where market makers lose the
            least money. Stocks often drift toward Max Pain into expiry, making it a useful anchor when
            choosing your strangle strikes. Pryvo shows Max Pain for every earnings event.
          </Typography>
        </Box>
      </Paper>

      {/* ── risk ──────────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderColor: 'warning.main', borderWidth: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>⚠️ The main risk</Typography>
        <Typography variant="body1">
          Sometimes the stock moves <em>more</em> than expected — a "gap through" your strike. This can
          produce losses larger than the credit received. Manage size (Pryvo suggests 1–3% of account
          per trade), always use the liquidity tier to avoid illiquid options, and consider defined-risk
          structures (iron condors) when in doubt.
        </Typography>
      </Paper>

    </DialogContent>
  </Dialog>
  );
};

export default EarningsGuide;
